import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { DocumentArrowUpIcon, TrashIcon } from '@heroicons/react/24/outline'
import { apiClient } from '../services/api'
import AnalyticsDetailModal from './AnalyticsDetailModal'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

export default function AdminPanel() {
  // Add robots noindex,nofollow for admin pages only
  useEffect(() => {
    const metaName = 'robots'
    let meta = document.querySelector(`meta[name="${metaName}"]`) as HTMLMetaElement | null
    const previousContent = meta?.getAttribute('content') ?? null

    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('name', metaName)
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', 'noindex, nofollow')

    return () => {
      if (!meta) return
      if (previousContent !== null) {
        meta.setAttribute('content', previousContent)
      } else {
        document.head.removeChild(meta)
      }
    }
  }, [])
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'import' | 'analytics' | 'add' | 'resources' | 'demo-videos'>('analytics')
  const [importMessage, setImportMessage] = useState('')
  const [isImporting, setIsImporting] = useState(false)
  const [analyticsModal, setAnalyticsModal] = useState<'contacts' | 'templates' | 'sends' | 'likes' | 'views' | null>(null)
  const [resourcesMessage, setResourcesMessage] = useState('')
  const [isImportingResources, setIsImportingResources] = useState(false)
  const [demoVideosMessage, setDemoVideosMessage] = useState('')
  const [isImportingDemoVideos, setIsImportingDemoVideos] = useState(false)
  
  // Single email form state
  const [singleTemplate, setSingleTemplate] = useState({
    name: '',
    description: '',
    esputnik_message_id: '',
    video_url: '',
    preview_image: '',
    gif_url: '',
    web_version_url: '',
    category: '',
    features: [] as string[],
    compatibility: { gmail: true, outlook: true, apple_mail: true },
    interactivity_level: 'medium'
  })
  const [isAdding, setIsAdding] = useState(false)
  const [addMessage, setAddMessage] = useState('')
  
  // Edit email state
  const [editingTemplate, setEditingTemplate] = useState<any>(null)
  const [isEditing, setIsEditing] = useState(false)

  // Check for edit parameter in URL
  useEffect(() => {
    const editId = searchParams.get('edit')
    if (editId) {
      // Load email for editing
      apiClient.getTemplate(Number(editId))
        .then(template => {
          setEditingTemplate(template)
          setIsEditing(true)
          // Clear the URL parameter
          searchParams.delete('edit')
          setSearchParams(searchParams)
        })
        .catch(error => {
          console.error('Error loading email for edit:', error)
        })
    }
  }, [searchParams, setSearchParams])

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['analytics'],
    queryFn: () => apiClient.getAnalytics(),
    enabled: activeTab === 'analytics'
  })

  const { data: dailyStats } = useQuery({
    queryKey: ['dailyStats'],
    queryFn: () => apiClient.getDailyStats(30),
    enabled: activeTab === 'analytics'
  })

  const { data: likeAnalytics } = useQuery({
    queryKey: ['likeAnalytics'],
    queryFn: () => apiClient.getLikeAnalytics(),
    enabled: activeTab === 'analytics'
  })

  const { data: viewAnalytics, error: viewAnalyticsError } = useQuery({
    queryKey: ['viewAnalytics'],
    queryFn: () => apiClient.getViewsAnalytics(),
    enabled: activeTab === 'analytics'
  })

  // Use likeAnalytics data in the analytics display
  const totalLikesFromAnalytics = likeAnalytics?.total_likes_from_templates || 0
  const totalViewsFromAnalytics = viewAnalytics?.total_views_from_templates || 0

  // Debug logging
  console.log('viewAnalytics:', viewAnalytics)
  console.log('viewAnalyticsError:', viewAnalyticsError)
  console.log('totalViewsFromAnalytics:', totalViewsFromAnalytics)

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient.getCategories()
  })

  const { data: resourceTypes = [] } = useQuery({
    queryKey: ['resourceTypes'],
    queryFn: () => apiClient.getResourceTypes()
  })

  // Resources pagination state
  const [resourcesPage, setResourcesPage] = useState(1)
  const [resourcesPageSize] = useState(20)
  const [resourcesTotal, setResourcesTotal] = useState(0)

  // Get resources count
  const { data: resourcesCountData } = useQuery({
    queryKey: ['resourcesCount'],
    queryFn: () => apiClient.getResourcesCount(),
    enabled: activeTab === 'resources'
  })

  const { data: allResources = [] } = useQuery({
    queryKey: ['allResources', resourcesPage, resourcesPageSize],
    queryFn: () => apiClient.getAllResources((resourcesPage - 1) * resourcesPageSize, resourcesPageSize),
    enabled: activeTab === 'resources'
  })

  // Update total count from API
  useEffect(() => {
    if (resourcesCountData) {
      setResourcesTotal(resourcesCountData.total)
    }
  }, [resourcesCountData])

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsImporting(true)
    setImportMessage('')

    try {
      const result = await apiClient.importTemplatesFromCSV(file)
      
      if (result.success) {
        setImportMessage(`Successfully imported ${result.imported_count} emails`)
      } else {
        setImportMessage(`Errors: ${result.errors.join(', ')}`)
      }
    } catch (error) {
      setImportMessage('Error importing file')
      console.error('Import error:', error)
    } finally {
      setIsImporting(false)
    }
  }

  const handleResourcesUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsImportingResources(true)
    setResourcesMessage('')

    try {
      const result = await apiClient.importResourcesFromCSV(file)
      
      if (result.success) {
        setResourcesMessage(`✅ Successfully imported ${result.imported} resources${result.skipped > 0 ? ` (skipped ${result.skipped})` : ''}`)
        // Refresh resources list and reset to first page
        setResourcesPage(1)
        queryClient.invalidateQueries({ queryKey: ['allResources'] })
        queryClient.invalidateQueries({ queryKey: ['resourcesCount'] })
      } else {
        setResourcesMessage(`❌ Import failed: ${result.errors?.join(', ') || 'Unknown error'}`)
      }
    } catch (error: any) {
      setResourcesMessage(`❌ Error: ${error.response?.data?.detail || error.message || 'Unknown error'}`)
      console.error('Resources import error:', error)
    } finally {
      setIsImportingResources(false)
      // Reset file input
      event.target.value = ''
    }
  }

  const handleDemoVideosUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsImportingDemoVideos(true)
    setDemoVideosMessage('')

    try {
      const result = await apiClient.importDemoVideosFromCSV(file)
      
      if (result.success) {
        const message = `✅ Successfully imported ${result.imported} demo videos${result.skipped > 0 ? ` (skipped ${result.skipped})` : ''}`
        setDemoVideosMessage(message)
        // Refresh demo videos for all templates
        queryClient.invalidateQueries({ queryKey: ['demoVideos'] })
      } else {
        setDemoVideosMessage(`❌ Import failed: ${result.errors?.join(', ') || 'Unknown error'}`)
      }
    } catch (error: any) {
      setDemoVideosMessage(`❌ Error: ${error.response?.data?.detail || error.message || 'Unknown error'}`)
      console.error('Demo videos import error:', error)
    } finally {
      setIsImportingDemoVideos(false)
      // Reset file input
      event.target.value = ''
    }
  }

  const handleDeleteResource = async (resourceId: number) => {
    if (!confirm('Are you sure you want to delete this resource?')) {
      return
    }

    try {
      await apiClient.deleteResource(resourceId)
      setResourcesMessage('✅ Resource deleted successfully')
      // Refresh resources list
      queryClient.invalidateQueries({ queryKey: ['allResources'] })
      queryClient.invalidateQueries({ queryKey: ['resourcesCount'] })
      
      // If we're on a page with no items left, go to previous page
      if (allResources.length === 1 && resourcesPage > 1) {
        setResourcesPage(resourcesPage - 1)
      }
    } catch (error: any) {
      setResourcesMessage(`❌ Error deleting: ${error.response?.data?.detail || error.message || 'Unknown error'}`)
      console.error('Delete resource error:', error)
    }
  }

  const sampleData = [
    {
      name: "Shake it game",
      description: "Interactive shake game with AMP support",
      esputnik_message_id: "4219566",
      video_url: "https://www.youtube.com/live/BezlQ2KgrJA?si=f2A3iDEzzM1psw7n",
      preview_image: "https://live-content-worker.support-ed7.workers.dev/media/article/14976/conversions/01jz5hkfwch6anwj0zpx3r46jz-690x384-webp.webp",
      category: "Game. Interactive & Discovery",
      features: "[\"AMP\", \"Kinetic\"]",
      compatibility: "{\"gmail\": true, \"outlook\": true, \"apple_mail\": true}",
      interactivity_level: "high"
    },
    {
      name: "Magic 8 ball",
      description: "Interactive fortune telling game",
      esputnik_message_id: "4219603",
      video_url: "https://www.youtube.com/live/BezlQ2KgrJA?si=f2A3iDEzzM1psw7n",
      preview_image: "https://live-content-worker.support-ed7.workers.dev/media/article/14976/conversions/01jz5hkfwch6anwj0zpx3r46jz-690x384-webp.webp",
      category: "Interactive. Discovery",
      features: "[\"AMP\", \"Kinetic\"]",
      compatibility: "{\"gmail\": true, \"outlook\": true, \"apple_mail\": true}",
      interactivity_level: "high"
    },
    {
      name: "Questionnaire",
      description: "Interactive survey with multiple choice questions",
      esputnik_message_id: "3964832",
      video_url: "https://www.youtube.com/live/BezlQ2KgrJA?si=f2A3iDEzzM1psw7n",
      preview_image: "https://live-content-worker.support-ed7.workers.dev/media/article/14976/conversions/01jz5hkfwch6anwj0zpx3r46jz-690x384-webp.webp",
      category: "Business.",
      features: "[\"AMP\", \"Kinetic\"]",
      compatibility: "{\"gmail\": true, \"outlook\": true, \"apple_mail\": true}",
      interactivity_level: "high"
    }
  ]

  const handleJSONImport = async () => {
    setIsImporting(true)
    setImportMessage('')

    try {
      const result = await apiClient.importTemplates(sampleData)
      
      if (result.success) {
        setImportMessage(`Successfully imported ${result.imported_count} emails`)
      } else {
        setImportMessage(`Errors: ${result.errors.join(', ')}`)
      }
    } catch (error) {
      setImportMessage('Error importing data')
      console.error('Import error:', error)
    } finally {
      setIsImporting(false)
    }
  }

  const handleAddSingleTemplate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsAdding(true)
    setAddMessage('')

    try {
      await apiClient.addTemplate(singleTemplate)
      
      setAddMessage('Template added successfully!')
      // Reset form
      setSingleTemplate({
        name: '',
        description: '',
        esputnik_message_id: '',
        video_url: '',
        preview_image: '',
        gif_url: '',
        web_version_url: '',
        category: '',
        features: [],
        compatibility: { gmail: true, outlook: true, apple_mail: true },
        interactivity_level: 'medium'
      })
    } catch (error: any) {
      setAddMessage(`Error adding email: ${error.response?.data?.detail || error.message}`)
      console.error('Add email error:', error)
    } finally {
      setIsAdding(false)
    }
  }

  const handleDeleteTemplate = async (templateId: number) => {
    if (!confirm('Are you sure you want to delete this email?')) {
      return
    }

    try {
      const result = await apiClient.deleteTemplate(templateId)
      
      if (result.success) {
        setAddMessage('Email deleted successfully!')
        // Refresh analytics
        window.location.reload()
      } else {
        setAddMessage(`Error: ${result.message}`)
      }
    } catch (error) {
      setAddMessage('Error deleting email')
      console.error('Delete email error:', error)
    }
  }

  const handleEditTemplate = (template: any) => {
    setEditingTemplate(template)
    setIsEditing(true)
  }

  const handleUpdateTemplate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsEditing(true)
    setAddMessage('')

    try {
      // Update template basic info
      await apiClient.updateTemplate(editingTemplate.id, {
        name: editingTemplate.name,
        description: editingTemplate.description,
        esputnik_message_id: editingTemplate.esputnik_message_id,
        video_url: editingTemplate.video_url,
        preview_image: editingTemplate.preview_image,
        gif_url: editingTemplate.gif_url,
        web_version_url: editingTemplate.web_version_url,
        category: editingTemplate.category,
        features: editingTemplate.features,
        compatibility: editingTemplate.compatibility,
        interactivity_level: editingTemplate.interactivity_level
      })
      
      // Update statistics separately
      await apiClient.updateTemplateStats(editingTemplate.id, {
        like_count: editingTemplate.like_count,
        view_count: editingTemplate.view_count,
        send_count: editingTemplate.send_count
      })
      
      setAddMessage('Email updated successfully!')
      setEditingTemplate(null)
      setIsEditing(false)
      // Refresh analytics
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error: any) {
      setAddMessage(`Error updating email: ${error.response?.data?.detail || error.message}`)
      console.error('Update email error:', error)
      setIsEditing(false)
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('add')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'add'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Add Email
            </button>
            <button
              onClick={() => setActiveTab('import')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'import'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Import Emails
            </button>
            <button
              onClick={() => setActiveTab('resources')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'resources'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Resources
            </button>
            <button
              onClick={() => setActiveTab('demo-videos')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'demo-videos'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Demo Videos
            </button>
          </nav>
        </div>

        <div className="p-6">
          {isEditing && editingTemplate ? (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-4">Edit Email</h2>
              
              <form onSubmit={handleUpdateTemplate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Name *
                    </label>
                    <input
                      type="text"
                      value={editingTemplate.name}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      eSputnik Message ID *
                    </label>
                    <input
                      type="text"
                      value={editingTemplate.esputnik_message_id}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, esputnik_message_id: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editingTemplate.description || ''}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    HTML is allowed (e.g. paragraphs, lists, links). It is sanitized when shown on the site.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video URL
                    </label>
                    <input
                      type="url"
                      value={editingTemplate.video_url || ''}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, video_url: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preview Image URL
                    </label>
                    <input
                      type="url"
                      value={editingTemplate.preview_image || ''}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, preview_image: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GIF Animation URL (for hover)
                    </label>
                    <input
                      type="url"
                      value={editingTemplate.gif_url || ''}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, gif_url: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://example.com/animation.gif"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Web Version URL
                    </label>
                    <input
                      type="url"
                      value={editingTemplate.web_version_url || ''}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, web_version_url: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://viewstripo.email/..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Category
                    </label>
                    <select
                      value={editingTemplate.category || ''}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, category: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select category</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Resource Type
                    </label>
                    <select
                      value={editingTemplate.resource_type || ''}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, resource_type: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select resource type</option>
                      {resourceTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fallback Stack
                    </label>
                    <div className="space-y-2">
                      {['AMP', 'Kinetic', 'Static'].map((feature) => (
                        <label key={feature} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={(editingTemplate.features || []).includes(feature)}
                            onChange={(e) => {
                              const features = editingTemplate.features || []
                              if (e.target.checked) {
                                setEditingTemplate({
                                  ...editingTemplate,
                                  features: [...features, feature]
                                })
                              } else {
                                setEditingTemplate({
                                  ...editingTemplate,
                                  features: features.filter((f: string) => f !== feature)
                                })
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm">{feature}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Statistics Section */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4">Statistics (for popularity index adjustment)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Likes
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={editingTemplate.like_count || 0}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, like_count: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Views
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={editingTemplate.view_count || 0}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, view_count: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sends
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={editingTemplate.send_count || 0}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, send_count: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Popularity Score: {editingTemplate.popularity_score || 0} (Views + Sends×2 + Likes×3 + Resources×2)
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTemplate(null)
                      setIsEditing(false)
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isAdding}
                    className="flex-1 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isAdding ? 'Updating...' : 'Update Email'}
                  </button>
                </div>
              </form>
            </div>
          ) : activeTab === 'analytics' ? (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-4">Analytics</h2>
              
              {analyticsLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : analytics ? (
                <>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <button
                      onClick={() => setAnalyticsModal('templates')}
                      className="bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors text-left"
                    >
                      <div className="text-2xl font-bold text-blue-600">{analytics.total_templates}</div>
                      <div className="text-sm text-blue-800">Total Emails</div>
                    </button>
                    <button
                      onClick={() => setAnalyticsModal('sends')}
                      className="bg-green-50 p-4 rounded-lg hover:bg-green-100 transition-colors text-left"
                    >
                      <div className="text-2xl font-bold text-green-600">{analytics.successful_sends}</div>
                      <div className="text-sm text-green-800">Successful Sends</div>
                    </button>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{analytics.failed_sends}</div>
                      <div className="text-sm text-red-800">Failed Sends</div>
                    </div>
                    <button
                      onClick={() => setAnalyticsModal('contacts')}
                      className="bg-purple-50 p-4 rounded-lg hover:bg-purple-100 transition-colors text-left"
                    >
                      <div className="text-2xl font-bold text-purple-600">{analytics.unique_contacts}</div>
                      <div className="text-sm text-purple-800">Unique Contacts</div>
                    </button>
                    <button
                      onClick={() => setAnalyticsModal('likes')}
                      className="bg-pink-50 p-4 rounded-lg hover:bg-pink-100 transition-colors text-left"
                    >
                      <div className="text-2xl font-bold text-pink-600">
                        {totalLikesFromAnalytics || analytics.popular_templates.reduce((sum: number, t: any) => sum + (t.like_count || 0), 0)}
                      </div>
                      <div className="text-sm text-pink-800">Total Likes</div>
                    </button>
                    <button
                      onClick={() => setAnalyticsModal('views')}
                      className="bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors text-left"
                    >
                      <div className="text-2xl font-bold text-blue-600">
                        {totalViewsFromAnalytics || 0}
                      </div>
                      <div className="text-sm text-blue-800">Total Views</div>
                    </button>
                  </div>

                  {/* Daily Activity Chart */}
                  {dailyStats && dailyStats.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold mb-4">Activity Over Last 30 Days</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dailyStats}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(date) => {
                              const d = new Date(date)
                              return `${d.getMonth() + 1}/${d.getDate()}`
                            }}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip 
                            labelFormatter={(date) => {
                              const d = new Date(date)
                              return d.toLocaleDateString()
                            }}
                          />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="views" 
                            stroke="#3B82F6" 
                            strokeWidth={2}
                            name="Views"
                            dot={{ r: 3 }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="sends" 
                            stroke="#10B981" 
                            strokeWidth={2}
                            name="Sends"
                            dot={{ r: 3 }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="likes" 
                            stroke="#EC4899" 
                            strokeWidth={2}
                            name="Likes"
                            dot={{ r: 3 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Popular Templates */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Popular Emails</h3>
                    <div className="space-y-2">
                      {analytics.popular_templates.map((template: any) => (
                        <div key={template.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium">{template.name}</div>
                            <div className="text-sm text-gray-600">
                              Views: {template.view_count} • Sends: {template.send_count} • Likes: {template.like_count || 0}
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <div className="font-bold text-primary-600">{template.popularity_score}</div>
                              <div className="text-xs text-gray-500">popularity</div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditTemplate(template)}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteTemplate(template.id)}
                                className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-gray-500">No data to display</p>
              )}
            </div>
          ) : activeTab === 'add' ? (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-4">Add Single Email</h2>
              
              <form onSubmit={handleAddSingleTemplate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Name *
                    </label>
                    <input
                      type="text"
                      value={singleTemplate.name}
                      onChange={(e) => setSingleTemplate({ ...singleTemplate, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      eSputnik Message ID *
                    </label>
                    <input
                      type="text"
                      value={singleTemplate.esputnik_message_id}
                      onChange={(e) => setSingleTemplate({ ...singleTemplate, esputnik_message_id: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={singleTemplate.description}
                    onChange={(e) => setSingleTemplate({ ...singleTemplate, description: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    HTML is allowed (e.g. paragraphs, lists, links). It is sanitized when shown on the site.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video URL
                    </label>
                    <input
                      type="url"
                      value={singleTemplate.video_url}
                      onChange={(e) => setSingleTemplate({ ...singleTemplate, video_url: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preview Image URL
                    </label>
                    <input
                      type="url"
                      value={singleTemplate.preview_image}
                      onChange={(e) => setSingleTemplate({ ...singleTemplate, preview_image: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GIF Animation URL (for hover)
                    </label>
                    <input
                      type="url"
                      value={singleTemplate.gif_url}
                      onChange={(e) => setSingleTemplate({ ...singleTemplate, gif_url: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://example.com/animation.gif"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Web Version URL
                    </label>
                    <input
                      type="url"
                      value={singleTemplate.web_version_url}
                      onChange={(e) => setSingleTemplate({ ...singleTemplate, web_version_url: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://viewstripo.email/..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Category
                    </label>
                    <select
                      value={singleTemplate.category}
                      onChange={(e) => setSingleTemplate({ ...singleTemplate, category: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select category</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Interactivity Level
                    </label>
                    <select
                      value={singleTemplate.interactivity_level}
                      onChange={(e) => setSingleTemplate({ ...singleTemplate, interactivity_level: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fallback Stack
                    </label>
                    <div className="space-y-2">
                      {['AMP', 'Kinetic', 'Static'].map((feature) => (
                        <label key={feature} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={singleTemplate.features.includes(feature)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSingleTemplate({
                                  ...singleTemplate,
                                  features: [...singleTemplate.features, feature]
                                })
                              } else {
                                setSingleTemplate({
                                  ...singleTemplate,
                                  features: singleTemplate.features.filter(f => f !== feature)
                                })
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm">{feature}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Client Compatibility
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(singleTemplate.compatibility).map(([client, supported]) => (
                      <label key={client} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={supported}
                          onChange={(e) => setSingleTemplate({
                            ...singleTemplate,
                            compatibility: {
                              ...singleTemplate.compatibility,
                              [client]: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span className="text-sm capitalize">{client}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {addMessage && (
                  <div className={`p-3 rounded-lg text-sm ${
                    addMessage.includes('successfully') 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {addMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isAdding}
                  className="w-full bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isAdding ? 'Adding...' : 'Add Email'}
                </button>
              </form>
            </div>
          ) : activeTab === 'import' ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">Import Emails from CSV</h2>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Click to upload CSV file
                      </span>
                      <input
                        id="file-upload"
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        className="sr-only"
                        disabled={isImporting}
                      />
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      CSV file with columns: name, description, esputnik_message_id, video_url, preview_image, category, features, compatibility, interactivity_level
                    </p>
                  </div>
                </div>

                {importMessage && (
                  <div className={`mt-4 p-3 rounded-lg text-sm ${
                    importMessage.includes('Successfully') 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {importMessage}
                  </div>
                )}
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Or import test data</h3>
                <button
                  onClick={handleJSONImport}
                  disabled={isImporting}
                  className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isImporting ? 'Importing...' : 'Import Test Data'}
                </button>
              </div>
            </div>
          ) : activeTab === 'resources' ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">Import Resources from CSV</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Upload a CSV file with additional resources (guides, videos, articles, etc.) for email templates.
                  Resources without a link will be ignored.
                </p>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <label htmlFor="resources-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Click to upload CSV file
                      </span>
                      <input
                        id="resources-upload"
                        type="file"
                        accept=".csv"
                        onChange={handleResourcesUpload}
                        className="sr-only"
                        disabled={isImportingResources}
                      />
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      CSV file with columns: esid, title, description, type, link
                    </p>
                    <div className="mt-3 text-xs text-gray-500 space-y-1">
                      <p><strong>esid:</strong> eSputnik message ID (matches template)</p>
                      <p><strong>type:</strong> Video, Article, How to, eBook, Guide, Template, Module, Case, Example, Webinar, Course, Builder, Other</p>
                      <p><strong>Note:</strong> Existing resources with same esid+link will be updated, new ones will be added</p>
                    </div>
                  </div>
                </div>

                {resourcesMessage && (
                  <div className={`mt-4 p-3 rounded-lg text-sm ${
                    resourcesMessage.includes('✅') 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {resourcesMessage}
                  </div>
                )}
              </div>

              {/* Resources List */}
              {allResources.length > 0 && (
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">
                      Existing Resources ({resourcesTotal > 0 ? `${(resourcesPage - 1) * resourcesPageSize + 1}-${Math.min(resourcesPage * resourcesPageSize, resourcesTotal)} of ${resourcesTotal}` : allResources.length})
                    </h3>
                    
                    {/* Pagination Controls */}
                    {resourcesTotal > resourcesPageSize && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setResourcesPage(Math.max(1, resourcesPage - 1))}
                          disabled={resourcesPage === 1}
                          className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                        >
                          ← Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {resourcesPage} of {Math.ceil(resourcesTotal / resourcesPageSize)}
                        </span>
                        <button
                          onClick={() => setResourcesPage(resourcesPage + 1)}
                          disabled={resourcesPage >= Math.ceil(resourcesTotal / resourcesPageSize)}
                          className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {allResources.map((resource: any) => (
                      <div key={resource.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2 py-0.5 rounded">
                              {resource.type}
                            </span>
                            <span className="text-xs text-gray-500">
                              esid: {resource.esputnik_message_id}
                            </span>
                          </div>
                          <h4 className="text-sm font-medium text-gray-900 mb-1">
                            {resource.title}
                          </h4>
                          {resource.description && (
                            <p className="text-sm text-gray-700 mb-1 line-clamp-2">
                              {resource.description}
                            </p>
                          )}
                          <a 
                            href={resource.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline truncate block"
                          >
                            {resource.link}
                          </a>
                        </div>
                        <button
                          onClick={() => handleDeleteResource(resource.id)}
                          className="ml-3 p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors flex-shrink-0"
                          title="Delete resource"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'demo-videos' ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">Import Demo Videos from CSV</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Upload a CSV file with demo videos for email templates. Videos will be displayed in the Demo tab of each template.
                  Duplicate videos (same esid + email_client) will be updated instead of creating duplicates.
                </p>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <label htmlFor="demo-videos-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Click to upload CSV file
                      </span>
                      <input
                        id="demo-videos-upload"
                        type="file"
                        accept=".csv"
                        onChange={handleDemoVideosUpload}
                        className="sr-only"
                        disabled={isImportingDemoVideos}
                      />
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      CSV file with columns: esid (or esputnik_message_id), youtube_link, name, email_client
                    </p>
                    <div className="mt-3 text-xs text-gray-500 space-y-1">
                      <p><strong>esid / esputnik_message_id:</strong> eSputnik message ID (string, matches template)</p>
                      <p><strong>youtube_link:</strong> YouTube URL or video ID (will be converted to embed format)</p>
                      <p><strong>name:</strong> Video name/description (optional, e.g., "Questionnaire demo", "Interactive elements")</p>
                      <p><strong>email_client:</strong> Email client name (e.g., "Gmail Desktop", "Outlook", "Apple Mail", etc.)</p>
                      <p><strong>Note:</strong> Videos with the same esid + email_client will be updated (deduplication)</p>
                      <p><strong>Example:</strong> 4219566, https://www.youtube.com/watch?v=dQw4w9WgXcQ, Questionnaire demo, Gmail Desktop</p>
                    </div>
                  </div>
                </div>

                {demoVideosMessage && (
                  <div className={`mt-4 p-3 rounded-lg text-sm ${
                    demoVideosMessage.includes('✅') 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {demoVideosMessage}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Analytics Detail Modal */}
      {analyticsModal && (
        <AnalyticsDetailModal
          type={analyticsModal}
          onClose={() => setAnalyticsModal(null)}
        />
      )}
    </div>
  )
}
